import type React from 'react';

import { Badge } from '@change/ds-core-ui/Badge';
import { Text } from '@change/ds-core-ui/Text';

import { useNow } from '@change-corgi/core-react/date';
import { Translate, useI18n } from '@change-corgi/core-react/i18n';
import { Link } from '@change-corgi/core-react/router';

import type { PetitionContentUpdate } from '#src/app/pages/petitionDetails/shared/types';

import { UpdateBody } from './UpdateBody';
import { UpdateMedia } from './UpdateMedia';

type Props = Readonly<{
	update: PetitionContentUpdate;
	newUpdate?: boolean;
}>;

export const UpdateCard = (({ ref, update, newUpdate = false }) => {
	const now = useNow();
	const { translate, localizeRelativeTime } = useI18n();

	const time = localizeRelativeTime(now, update.createdAt);
	const author = update.author?.displayName ? ` • ${update.author.displayName}` : '';
	const hasUpdateType = ['starter', 'dmResponse', 'staff'].includes(update.type);

	return (
		<Link
			to={update.url}
			className="hover:[&>a]:text-primary-black focus:[&>a]:text-primary-black block rounded-md text-inherit no-underline"
			ref={ref}
			data-qa={`petition-update-${update.type}`}
		>
			<div className="border-stroke-weaker bg-background-base overflow-hidden rounded-md border">
				<div className="flex gap-4 p-4">
					<UpdateMedia update={update} />

					<div className="flex-1">
						<div className="flex flex-row">
							<Text
								as="div"
								className="text-typography-strong min-w-0 flex-1"
								fontWeight="bold"
								size="large"
								variant="body"
							>
								{update.title}
							</Text>
							{newUpdate && (
								<Badge
									className="ml-2 h-6 shrink-0"
									size="small"
									variant="information"
									emphasis="weaker"
									label={translate('corgi.petition.details.updates.badge_new_label')}
								/>
							)}
						</div>
						<div>
							<Text
								as="div"
								className="mb-2"
								color="typography-secondary"
								size="small"
								variant="body"
								fontWeight="light"
							>
								{`${time}${author}`}
								{hasUpdateType && ', '}
								{update.type === 'starter' && <Translate value="corgi.petition.details.updates.petition_starter" />}
								{update.type === 'dmResponse' && (
									<Translate value="corgi.petition.details.updates.decision_maker_response" />
								)}
								{update.type === 'staff' && <Translate value="corgi.petition.details.updates.staff_member" />}
							</Text>
						</div>
						<UpdateBody update={update} />
					</div>
				</div>
			</div>
		</Link>
	);
}) as React.FC<
	Props & {
		ref?: React.RefObject<HTMLAnchorElement>;
	}
>;
